import { BNBox, BNStack, resolveUrl, sxEnsureArray } from "@bn-monorepo/js-packages";
import { BoxProps, useTheme } from "@mui/material";
import { MenuOutlined } from "@mui/icons-material";
import { useContext } from "react";
import { BNHeaderMegaMenuExpandable } from "./BNHeaderMegaMenuExpandable";
import { BNHeaderMegaMenuLink } from "./BNHeaderMegaMenuLink";
import { MegaMenu } from "../../CmsContent/services";
import { SettingsContext } from "../../../settingsContext";
import HeaderButton from "@bn-monorepo/js-packages/src/Components/BNHeader/HeaderButton";

interface Props {
  list: MegaMenu["list"];
  menuOpenLabel: string;
  sx?: BoxProps["sx"];
}

export const BNHeaderMegaMenuMobile: React.FC<Props> = ({ list, menuOpenLabel, sx }) => {
  const { env } = useContext(SettingsContext);
  const theme = useTheme();
  const headerButtonColorSx = [
    { color: "tertiary.main" },

    theme.applyStyles("dark", {
      color: "white",
    }),
  ];
  return (
    <BNBox
      sx={[
        {
          "&:has([popover]:popover-open) #open-mega-menu": {
            backgroundColor: "secondary.light",
          },
        },
        ...sxEnsureArray(sx),
      ]}
    >
      <HeaderButton
        buttonId="open-mega-menu"
        text={menuOpenLabel}
        icon={MenuOutlined}
        ariaLabel={menuOpenLabel}
        iconButtonSx={headerButtonColorSx}
        dataUit="open-mega-menu"
        popoverTarget="megamenu-mobile-popup"
        hasPopup
        clickHandlerOverride={null}
      />
      <BNBox
        popover=""
        id="megamenu-mobile-popup"
        sx={{
          margin: 0,
          top: "5.68rem",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100vw",
          maxHeight: "calc(100vh - 6rem)",
          overflowY: "auto",
          border: 0,
          backgroundColor: "transparent",
          "@keyframes fadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
          animation: "fadeIn 300ms ease-in-out forwards",
          "&::backdrop": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            clipPath: "inset(6rem 0 0 0)",
            animation: "fadeIn 300ms ease-in-out forwards",
          },
        }}
      >
        <BNStack
          component={"nav"}
          data-uit-container={"top-navigation"}
          sx={{
            gap: "0.2rem",
            backgroundColor: "background.paper",
            borderBottom: `0.1rem solid ${theme.vars.palette.secondary.light}`,
            p: 2,
            overflowY: "auto",
            borderBottomLeftRadius: theme.shape.borderRadius * 4,
            borderBottomRightRadius: theme.shape.borderRadius * 4,

            "& > :first-child .MuiButtonBase-root:first-of-type, & > .MuiButtonBase-root:first-child": {
              borderTopLeftRadius: theme.shape.borderRadius * 2,
              borderTopRightRadius: theme.shape.borderRadius * 2,
            },

            "& > :last-child .MuiButtonBase-root:first-of-type, & > .MuiButtonBase-root:last-child": {
              borderBottomLeftRadius: theme.shape.borderRadius * 2,
              borderBottomRightRadius: theme.shape.borderRadius * 2,
            },
          }}
        >
          {list
            .filter((level1) => !level1.hidden.mobile)
            .map((level1) => {
              if (level1.list.length) {
                return (
                  <BNHeaderMegaMenuExpandable
                    key={level1.link.url}
                    link={level1.link}
                    iconInlineSvg={level1.iconInlineSvg}
                  >
                    {level1.list
                      .filter((level2) => !level2.hidden.mobile)
                      .map((level2) => (
                        <BNHeaderMegaMenuLink
                          level={2}
                          to={resolveUrl(level2.link.url)}
                          key={level2.link.url}
                          iconInlineSvg={level2.iconInlineSvg}
                          data-cms-target-key={env !== "prod" ? level2.link.udi : undefined}
                          showArrowForward
                        >
                          {level2.link.name}
                        </BNHeaderMegaMenuLink>
                      ))}
                  </BNHeaderMegaMenuExpandable>
                );
              }

              return (
                level1.link && (
                  <BNHeaderMegaMenuLink
                    level={1}
                    to={resolveUrl(level1.link.url)}
                    key={level1.link.url}
                    iconInlineSvg={level1.iconInlineSvg}
                    data-cms-target-key={env !== "prod" ? level1.link.udi : undefined}
                    showArrowForward
                  >
                    {level1.link.name}
                  </BNHeaderMegaMenuLink>
                )
              );
            })}
        </BNStack>
      </BNBox>
    </BNBox>
  );
};
